import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { devtools, persist } from 'zustand/middleware';
import { AppState } from './app-state';
import { createMetaSlice } from '../features/run-sheet/meta-slice';
import { createRunSheetSlice } from '../features/run-sheet/store';
import { createPersonnelSlice } from '../features/personnel/store';
import { createSchedulerSlice } from '../features/scheduler/store';
import { temporal } from 'zundo';
import { createPropsSlice } from '../features/props/store';
import { createSoundSlice } from '../features/sound/store';
import { createProductionSlice } from '../features/run-sheet/production-slice';
import { createNoteSlice } from '../features/notes/store';
import { createFeedSlice } from '../features/feed/store';
import { createFilesSlice } from '../features/files/store';
import { createAdminSlice } from './admin-slice';
import { createCostumesSlice } from '../features/costumes/store';
import { createShowStructureSlice } from '../features/show-structure/store';
import { createActivityLogSlice } from '../features/activity-log/store';
import { createLightingSlice } from '../features/lighting/store';
import { createPromptBookSlice } from '../features/prompt-book/store';
import { createAttendanceSlice } from '../features/attendance/store';
import { createPerformanceSlice } from '../features/performance/store';
import { createFlySlice } from '../features/fly/store';
import { createProjectionsSlice } from '../features/projections/store';
import { createActionItemsSlice } from '../features/action-items/store';
import { createTranscriptionSlice } from '../features/transcription/store';
import { normalizeNoteType } from '../features/notes/note-type';
import {
    getToolByRoute,
    isToolId,
    WORKSPACES,
    type ToolId,
    type WorkspaceId,
} from '../features/layout/workspace-config';
import type { WorkspaceToolOrderOverrides } from '../features/layout/workspace-tool-order';

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const WORKSPACE_BY_ID = new Map(WORKSPACES.map((workspace) => [workspace.id, workspace] as const));

const resolvePersistedToolId = (value: unknown): ToolId | null => {
    if (typeof value !== 'string') {
        return null;
    }

    if (isToolId(value)) {
        return value;
    }

    const routeCandidate = value.startsWith('/') ? value : `/${value}`;
    return getToolByRoute(routeCandidate)?.id ?? null;
};

const normalizePersistedToolId = (value: unknown): ToolId => resolvePersistedToolId(value) ?? 'show-structure';

const normalizePersistedWorkspaceId = (value: unknown): WorkspaceId | null => {
    if (typeof value !== 'string') {
        return null;
    }

    return WORKSPACE_BY_ID.has(value as WorkspaceId) ? value as WorkspaceId : null;
};

// fallow-ignore-next-line complexity
const sanitizeWorkspaceToolOrderOverrides = (value: unknown): WorkspaceToolOrderOverrides => {
    if (!isRecord(value)) {
        return {};
    }

    const overrides: WorkspaceToolOrderOverrides = {};

    for (const [workspaceId, order] of Object.entries(value)) {
        const workspace = WORKSPACE_BY_ID.get(workspaceId as WorkspaceId);

        if (!workspace || !Array.isArray(order)) {
            continue;
        }

        const primaryToolIds = new Set<ToolId>(workspace.primaryTools);
        const normalizedOrder: ToolId[] = [];
        const normalizedOrderSet = new Set<ToolId>();

        for (const toolId of order) {
            if (typeof toolId !== 'string') {
                continue;
            }

            const normalizedToolId = resolvePersistedToolId(toolId);

            if (!normalizedToolId || !primaryToolIds.has(normalizedToolId) || normalizedOrderSet.has(normalizedToolId)) {
                continue;
            }

            normalizedOrder.push(normalizedToolId);
            normalizedOrderSet.add(normalizedToolId);
        }

        if (normalizedOrder.length > 0) {
            overrides[workspace.id] = normalizedOrder;
        }
    }

    return overrides;
};

const sanitizeStringRecord = (value: unknown): Record<string, string> => {
    if (!isRecord(value)) {
        return {};
    }

    const sanitized: Record<string, string> = {};

    for (const [key, recordValue] of Object.entries(value)) {
        if (key && typeof recordValue === 'string' && recordValue) {
            sanitized[key] = recordValue;
        }
    }

    return sanitized;
};

const sanitizeNumberRecord = (value: unknown): Record<string, number> => {
    if (!isRecord(value)) {
        return {};
    }

    const sanitized: Record<string, number> = {};

    for (const [key, recordValue] of Object.entries(value)) {
        if (key && typeof recordValue === 'number' && Number.isFinite(recordValue)) {
            sanitized[key] = Math.max(0, Math.round(recordValue));
        }
    }

    return sanitized;
};

const CLOUD_AUTHORITATIVE_PROJECT_KEYS = new Set([
    'projectMeta',
    'showStructure',
    'rows',
    'personnel',
    'trackingColumns',
    'castAssignments',
    'props',
    'rehearsalSchedule',
    'rehearsalScenes',
    'soundClips',
    'soundCues',
    'micAssignments',
    'micSwaps',
    'micPackConfig',
    'sets',
    'blocking',
    'choreography',
    'assets',
    'notes',
    'feedPosts',
    'feedComments',
    'feedReactions',
    'lastViewedFeedAt',
    'files',
    'currentFolderId',
    'selectedFileIds',
    'storageQuota',
    'showTrash',
    'cachedFileIds',
    'admin',
    'costumes',
    'activityLog',
    'activityFilters',
    'attendanceRecords',
    'performance',
    'lightingCues',
    'followSpotCues',
    'followSpots',
    'cueAnchors',
    'customCues',
    'flyCues',
    'projectionCues',
    'actionItems',
    'actionItemEvents',
    'actionItemDrafts',
    'actionItemsManagementStatus',
    'reviewedActionItemEvidenceKeys',
    'transcriptSessions',
    'transcriptSegmentsBySession',
    'transcriptAnchorsBySession',
    'speakerAttributionsBySession',
    'activeTranscriptSessionId',
    'displayedTranscriptSessionIdsBySurface',
]);

const UNDO_EXCLUDED_RUNTIME_AUTHORITY_KEYS = new Set([
    'transcriptSessions',
    'transcriptSegmentsBySession',
    'transcriptAnchorsBySession',
    'speakerAttributionsBySession',
    'activeTranscriptSessionId',
    'displayedTranscriptSessionIdsBySurface',
    'actionItemsManagementStatus',
    'reviewedActionItemEvidenceKeys',
]);

const omitRuntimeCloudFlags = (cloud: Record<string, unknown>): Record<string, unknown> => {
    const cloudRest: Record<string, unknown> = { ...cloud };
    delete cloudRest.hasLoadedCloudProject;
    delete cloudRest.hasLoadedProjectAccess;
    delete cloudRest.hasLoadedPersonnel;
    return cloudRest;
};

const isPersistedCloudProjectSession = (state: Record<string, unknown>): boolean => {
    if (!isRecord(state.cloud)) {
        return false;
    }

    return state.cloud.enabled === true
        && typeof state.cloud.orgId === 'string'
        && typeof state.cloud.projectId === 'string';
};

const omitCloudAuthoritativeProjectState = (state: Record<string, unknown>): Record<string, unknown> => {
    const cloudState = { ...state };

    for (const key of CLOUD_AUTHORITATIVE_PROJECT_KEYS) {
        delete cloudState[key];
    }

    return cloudState;
};

const removePersistedActionItemsAgendaState = (state: Record<string, unknown>): void => {
    delete state.productionAgendaDrafts;

    if (!isRecord(state.ui) || !isRecord(state.ui.surfaceSubtabs)) {
        return;
    }

    if (state.ui.surfaceSubtabs['action-items'] === 'agenda') {
        delete state.ui.surfaceSubtabs['action-items'];
    }
};

const normalizePersistedNoteEntries = (notes: unknown): AppState['notes'] | undefined => {
    if (!Array.isArray(notes)) {
        return;
    }

    return notes.flatMap(note => {
        if (!isRecord(note)) {
            return [];
        }

        return [{
            ...(note as unknown as AppState['notes'][number]),
            type: normalizeNoteType(note.type),
        }];
    });
};

const normalizePersistedNotes = (state: Record<string, unknown>): void => {
    const notes = normalizePersistedNoteEntries(state.notes);
    if (notes === undefined) {
        return;
    }

    state.notes = notes;
};

const migratePersistedState = (persistedState: unknown, version: number): unknown => {
    if (!isRecord(persistedState)) {
        return persistedState;
    }

    if (version < 4 && !Object.hasOwn(persistedState, 'choreography')) {
        persistedState.choreography = [];
    }

    if (version < 6 && isRecord(persistedState.cloud) && !Object.hasOwn(persistedState.cloud, 'member')) {
            persistedState.cloud.member = null;
        }

    if (version < 8) {
        delete persistedState.feedPosts;
        delete persistedState.feedComments;
        delete persistedState.feedReactions;
    }

    if (version < 9 && isRecord(persistedState.ui)) {
        persistedState.ui.workspaceToolOrderOverrides = sanitizeWorkspaceToolOrderOverrides(
            persistedState.ui.workspaceToolOrderOverrides,
        );
    }

    if (version < 10 && isRecord(persistedState.ui)) {
        persistedState.ui.surfaceScrollPositions = sanitizeNumberRecord(
            persistedState.ui.surfaceScrollPositions,
        );
        persistedState.ui.surfaceSubtabs = sanitizeStringRecord(
            persistedState.ui.surfaceSubtabs,
        );
    }

    if (version < 12) {
        removePersistedActionItemsAgendaState(persistedState);
    }

    normalizePersistedNotes(persistedState);

    for (const key of Object.keys(persistedState)) {
        if (key.endsWith('Actions')) {
            delete persistedState[key];
        }
    }

    return persistedState;
};

export const partializeAppState = (state: AppState): Record<string, unknown> => {
    const persistedState: Record<string, unknown> = {};
    const isCloudProjectSession = state.cloud.enabled === true
        && typeof state.cloud.orgId === 'string'
        && typeof state.cloud.projectId === 'string';
    const excludeKeys = new Set([
        'feedPosts',
        'feedComments',
        'feedReactions',
        'lightingCues',
        'followSpotCues',
        'followSpots',
        'cueAnchors',
        'customCues',
        'attendanceRecords',
        'performance',
    ]);
    const entries = Object.entries(state) as [Extract<keyof AppState, string>, AppState[keyof AppState]][];

    for (const [key, value] of entries) {
        if (
            key.endsWith('Actions')
            || excludeKeys.has(key)
            || (isCloudProjectSession && CLOUD_AUTHORITATIVE_PROJECT_KEYS.has(key))
        ) {
            continue;
        }

        if (key === 'auth' && isRecord(value)) {
            const authRest: Record<string, unknown> = { ...value };
            delete authRest.initialized;
            persistedState.auth = authRest;
            continue;
        }

        if (key === 'cloud' && isRecord(value)) {
            persistedState.cloud = omitRuntimeCloudFlags(value);
            continue;
        }

        if (key === 'showStructure' && isRecord(value)) {
            const showStructureRest: Record<string, unknown> = { ...value };
            delete showStructureRest.scriptBlocks;
            persistedState.showStructure = showStructureRest;
            continue;
        }

        persistedState[key] = value;
    }

    return persistedState;
};

export const partializeUndoableAppState = (state: AppState): Record<string, unknown> => {
    const filtered: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(state)) {
        if (
            !key.endsWith('Actions') &&
            key !== 'cloud' &&
            typeof value !== 'function' &&
            !UNDO_EXCLUDED_RUNTIME_AUTHORITY_KEYS.has(key)
        ) {
            filtered[key] = value;
        }
    }
    return filtered;
};

export const areUndoableAppStatesEqual = (
    pastState: Record<string, unknown>,
    currentState: Record<string, unknown>,
): boolean => {
    const pastKeys = Object.keys(pastState);
    const currentKeys = Object.keys(currentState);

    if (pastKeys.length !== currentKeys.length) {
        return false;
    }

    return pastKeys.every((key) => (
        Object.hasOwn(currentState, key)
        && Object.is(pastState[key], currentState[key])
    ));
};

export const mergePersistedAppState = (
    persistedState: unknown,
    currentState: AppState,
): AppState => {
    if (!isRecord(persistedState)) {
        return currentState;
    }

    const stateForMerge = {
        ...(isPersistedCloudProjectSession(persistedState)
            ? omitCloudAuthoritativeProjectState(persistedState)
            : persistedState),
    };
    normalizePersistedNotes(stateForMerge);

    const mergedState = {
        ...currentState,
        ...stateForMerge,
    } as AppState;
    mergedState.actionItemsManagementStatus = 'loading';
    mergedState.reviewedActionItemEvidenceKeys = [];

    if (isRecord(stateForMerge.auth)) {
        mergedState.auth = {
            ...currentState.auth,
            ...stateForMerge.auth,
        } as AppState['auth'];
    }

    if (isRecord(stateForMerge.cloud)) {
        const persistedCloud = omitRuntimeCloudFlags(stateForMerge.cloud);

        mergedState.cloud = {
            ...currentState.cloud,
            ...persistedCloud,
        } as AppState['cloud'];
    }

    if (isRecord(stateForMerge.ui)) {
        const persistedUi = { ...stateForMerge.ui };

        mergedState.ui = {
            ...currentState.ui,
            ...persistedUi,
            activeTab: normalizePersistedToolId(persistedUi.activeTab),
            activeWorkspace: normalizePersistedWorkspaceId(persistedUi.activeWorkspace),
            surfaceScrollPositions: sanitizeNumberRecord(
                persistedUi.surfaceScrollPositions,
            ),
            surfaceSubtabs: sanitizeStringRecord(
                persistedUi.surfaceSubtabs,
            ),
            workspaceToolOrderOverrides: sanitizeWorkspaceToolOrderOverrides(
                persistedUi.workspaceToolOrderOverrides,
            ),
        } as AppState['ui'];
    }

    if (isRecord(stateForMerge.showStructure)) {
        const persistedShowStructure = { ...stateForMerge.showStructure };
        delete persistedShowStructure.scriptBlocks;

        mergedState.showStructure = {
            ...currentState.showStructure,
            ...persistedShowStructure,
            scriptBlocks: currentState.showStructure.scriptBlocks ?? [],
        } as AppState['showStructure'];
    }

    return mergedState;
};

export const useAppStore = create<AppState>()(
    devtools(
        temporal(
            persist(
                immer((...a) => ({
                    ...createMetaSlice(...a),
                    ...createRunSheetSlice(...a),
                    ...createPersonnelSlice(...a),
                    ...createSchedulerSlice(...a),
                    ...createPropsSlice(...a),
                    ...createSoundSlice(...a),
                    ...createProductionSlice(...a),
                    ...createNoteSlice(...a),
                    ...createFeedSlice(...a),
                    ...createFilesSlice(...a),
                    ...createAdminSlice(...a),
                    ...createCostumesSlice(...a),
                    ...createShowStructureSlice(...a),
                    ...createActivityLogSlice(...a),
                    ...createLightingSlice(...a),
                    ...createPromptBookSlice(...a),
                    ...createAttendanceSlice(...a),
                    ...createPerformanceSlice(...a),
                    ...createFlySlice(...a),
                    ...createProjectionsSlice(...a),
                    ...createActionItemsSlice(...a),
                    ...createTranscriptionSlice(...a),
                })),
                {
                    name: 'onbook-storage',
                    version: 12,
                    migrate: migratePersistedState,
                    partialize: partializeAppState,
                    merge: mergePersistedAppState
                }
            ),
            {
                // Zundo configuration
                limit: 100,
                partialize: partializeUndoableAppState,
                equality: areUndoableAppStatesEqual,
            }
        )
    )
);
